/**
 * Offline Sync Service
 * Handles syncing offline actions with the server
 */

import axios from 'axios';
import {
  initOfflineDB,
  getPendingActions,
  markActionSynced,
  deleteAction,
  clearSyncedActions,
  getPendingCount
} from './offlineStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Sync a single action with the server
 */
const syncAction = async (action) => {
  try {
    let response;
    
    switch (action.action_type) {
      case 'clock_in':
        response = await axios.post(`${API}/time-entries/clock-in`, {
          latitude: action.latitude,
          longitude: action.longitude,
          project_id: action.project_id,
          offline: true,
          offline_timestamp: action.device_time,
          offline_location: action.offline_location
        });
        break;
        
      case 'clock_out':
        response = await axios.post(`${API}/time-entries/clock-out`, {
          offline: true,
          offline_timestamp: action.device_time,
          offline_location: action.offline_location
        });
        break;
        
      case 'break_start':
        response = await axios.post(`${API}/breaks/start`, {
          break_type: action.break_type,
          offline: true,
          offline_timestamp: action.device_time
        });
        break;
        
      case 'break_end':
        response = await axios.post(`${API}/breaks/end`, {
          offline: true,
          offline_timestamp: action.device_time
        });
        break;
        
      default:
        console.warn('Unknown action type:', action.action_type);
        return { success: false, error: 'Unknown action type' };
    }
    
    return { success: true, data: response.data };
  } catch (error) {
    // Check if it's a "business logic" error (e.g., already clocked in)
    // vs a network error
    if (error.response) {
      // Server responded with error - this action can't be synced
      // (e.g., already clocked in, not clocked in, etc.)
      return { 
        success: false, 
        error: error.response.data?.detail || 'Server error',
        permanent: true // Don't retry
      };
    } else {
      // Network error - can retry
      return { 
        success: false, 
        error: error.message || 'Network error',
        permanent: false // Can retry
      };
    }
  }
};

/**
 * Sync all pending actions with the server
 * Returns: { synced: number, failed: number, remaining: number }
 */
export const syncAllPendingActions = async (onProgress = null) => {
  if (!navigator.onLine) {
    console.log('Cannot sync - offline');
    return { synced: 0, failed: 0, remaining: await getPendingCount() };
  }
  
  await initOfflineDB();
  const pendingActions = await getPendingActions();
  
  if (pendingActions.length === 0) {
    return { synced: 0, failed: 0, remaining: 0 };
  }
  
  console.log(`Syncing ${pendingActions.length} pending actions...`);
  
  let synced = 0;
  let failed = 0;
  
  for (const action of pendingActions) {
    const result = await syncAction(action);
    
    if (result.success) {
      await markActionSynced(action.id, result.data);
      synced++;
      console.log(`Synced: ${action.action_type}`);
    } else if (result.permanent) {
      // Permanent failure - mark as synced to remove from queue
      await markActionSynced(action.id, { error: result.error });
      failed++;
      console.warn(`Permanent failure for ${action.action_type}: ${result.error}`);
    } else {
      // Temporary failure - keep in queue for retry
      failed++;
      console.warn(`Temporary failure for ${action.action_type}: ${result.error}`);
    }
    
    if (onProgress) {
      onProgress({ synced, failed, total: pendingActions.length });
    }
  }
  
  // Clean up synced actions
  await clearSyncedActions();
  
  const remaining = await getPendingCount();
  
  return { synced, failed, remaining };
};

/**
 * Set up automatic sync when coming back online
 */
export const setupAutoSync = (onSyncComplete = null) => {
  const handleOnline = async () => {
    console.log('Back online - starting sync...');
    try {
      const result = await syncAllPendingActions();
      console.log('Sync complete:', result);
      if (onSyncComplete) {
        onSyncComplete(result);
      }
    } catch (error) {
      console.error('Sync failed:', error);
    }
  };
  
  window.addEventListener('online', handleOnline);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
  };
};

/**
 * Check and sync on app startup
 */
export const syncOnStartup = async () => {
  if (!navigator.onLine) {
    const pending = await getPendingCount();
    if (pending > 0) {
      console.log(`${pending} actions pending sync (currently offline)`);
    }
    return { synced: 0, failed: 0, remaining: pending };
  }
  
  return syncAllPendingActions();
};

export default {
  syncAllPendingActions,
  setupAutoSync,
  syncOnStartup
};
